import * as React from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";

import {
  Box,
  Button,
  Drawer,
  IconButton,
  useTheme
} from "@mui/material/";
import MenuIcon from "@mui/icons-material/Menu";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";

function Navbar({ mode, toggleColorMode }) {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const scrollToSection = (sectionId) => {
    const sectionElement = document.getElementById(sectionId);
    const offset = 100;
    if (sectionElement) {
      const targetScroll = sectionElement.offsetTop - offset;
      window.scrollTo({
        top: targetScroll,
        behavior: "smooth",
      });
      setOpen(false);
    }
  };

  const menuItems = ['home', 'experiences', 'skills', 'projects'];

  return (
    <Box
      component={motion.div}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      sx={{
        position: 'fixed',
        top: 24,
        left: 0,
        right: 0,
        zIndex: 1100,
        display: 'flex',
        justifyContent: 'center',
        px: 2
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: theme.palette.background.paper, // This is rgba(255,255,255,0.03) from theme
          backdropFilter: "blur(20px)",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "99px",
          px: { xs: 2, md: 3 },
          py: 1.5,
          width: { xs: "100%", sm: "auto" },
          maxWidth: 800,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
        }}
      >
        {/* Theme Toggle (Replaced Logo) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mr: { md: 6 }
          }}
        >
          <IconButton 
            onClick={toggleColorMode} 
            color="inherit" 
            aria-label="Toggle theme" 
            sx={{ color: "text.primary" }}
          >
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Box>

        {/* Desktop Menu */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 1
          }}
        >
          {menuItems.map((section) => (
            <Button
              key={section}
              variant="text"
              onClick={() => scrollToSection(section)}
              disableRipple
              sx={{ 
                color: "text.secondary",
                textTransform: "capitalize",
                px: 2,
                "&:hover": {
                  color: "text.primary",
                  bgcolor: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"
                }
              }}
            >
              {section}
            </Button>
          ))}
        </Box>

        {/* Action Button */}
        <Box sx={{ display: { xs: "none", md: "flex" }, ml: 2, alignItems: "center", gap: 2 }}>
          <Button 
            variant="contained" 
            href="mailto:haritsraihan73@gmail.com"
          >
            Contact
          </Button>
        </Box>

        {/* Mobile menu */}
        <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1 }}>
          <IconButton
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{ color: "text.primary" }}
          >
            <MenuIcon />
          </IconButton>
          <Drawer 
            anchor="bottom" 
            open={open} 
            onClose={toggleDrawer(false)}
            PaperProps={{
              sx: {
                bgcolor: theme.palette.background.paper,
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
                borderTop: `1px solid ${theme.palette.divider}`,
              }
            }}
          >
            <Box
              sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box sx={{ width: 40, height: 4, bgcolor: "divider", borderRadius: 2, mx: "auto", mb: 2 }} />
              {menuItems.map((section) => (
                <Button
                  key={section}
                  variant="text" 
                  onClick={() => scrollToSection(section)}
                  sx={{ 
                    justifyContent: "flex-start",
                    color: 'text.secondary',
                    fontSize: "1.25rem",
                    textTransform: "capitalize",
                    py: 2,
                    "&:hover": { color: 'text.primary', bgcolor: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)" }
                  }}
                >
                  {section}
                </Button>
              ))}
              <Button 
                variant="contained" 
                href="mailto:haritsraihan73@gmail.com"
                sx={{ mt: 2, py: 2 }}
              >
                Contact Me
              </Button>
            </Box>
          </Drawer>
        </Box>
      </Box>
    </Box>
  );
}

Navbar.propTypes = {
  mode: PropTypes.oneOf(["dark", "light"]).isRequired,
  toggleColorMode: PropTypes.func.isRequired,
};

export default Navbar;
